import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
// Images
import img1 from '@/assets/decorative/benefits/01-baixar-app.jpg';
import img2 from '@/assets/decorative/benefits/02-criar-conta.jpg';
import img3 from '@/assets/decorative/benefits/03-descobrir-cafes.jpg';
import img4 from '@/assets/decorative/benefits/04-cafeterias-proximas.jpg';
import img5 from '@/assets/decorative/benefits/05-saboreia-cafes.jpg';
import img6 from '@/assets/decorative/benefits/06-fidelidade-ativa.png';
import img7 from '@/assets/decorative/benefits/07-cafe-de-volta.jpg';
import img8 from '@/assets/decorative/benefits/08-ranking-cafe.jpg';

export function BenefitsDesktopComposition() {
    const { t } = useTranslation();

    const fadeInUp = {
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
    };

    return (
        <div className="relative w-full max-w-[1200px] mx-auto hidden xl:block h-[800px] bg-transparent">
            {/* Background Gradient Layer */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#5D4037] via-[#8B7355] to-[#D7CCC8] rounded-[40px] opacity-20 -z-20 blur-2xl scale-[0.98] transform translate-y-4" />

            {/* Connecting Lines SVG Layer */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-visible" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    <linearGradient id="coffeeFlowGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#C4A77D" stopOpacity="0.4" />
                        <stop offset="50%" stopColor="#8B7355" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#C4A77D" stopOpacity="0.4" />
                    </linearGradient>
                    <filter id="glow">
                        <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>
                </defs>

                {/* Main Flow Line - The "Coffee Stream" */}
                {/* Path connects cards in narrative order: Baixar -> Criar -> Descubra -> Perto -> QR -> Fidelidade -> Volta -> Grão Mestre */}
                <motion.path
                    d="M 150 650 
                       C 80 500, 80 300, 150 200 
                       C 250 150, 350 180, 450 200 
                       C 550 220, 650 180, 750 200 
                       C 900 250, 850 480, 600 480 
                       C 450 480, 350 720, 450 720 
                       C 550 720, 650 720, 750 720 
                       C 950 720, 950 500, 1050 400"
                    fill="none"
                    stroke="url(#coffeeFlowGrad)"
                    strokeWidth="4"
                    strokeLinecap="round"
                    filter="url(#glow)"
                    initial={{ pathLength: 0, opacity: 0 }}
                    whileInView={{ pathLength: 1, opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 3, ease: "easeInOut" }}
                />

                {/* Secondary accent lines for liquid feel */}
                <motion.path
                    d="M 160 660 C 100 520, 100 320, 160 210"
                    fill="none" stroke="#D7CCC8" strokeWidth="1" opacity="0.5"
                    initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 3.5, delay: 0.2 }}
                />
                <motion.path
                    d="M 450 210 C 550 230, 650 190, 760 210"
                    fill="none" stroke="#D7CCC8" strokeWidth="1" opacity="0.5"
                    initial={{ pathLength: 0 }} whileInView={{ pathLength: 1 }} transition={{ duration: 3.5, delay: 0.5 }}
                />
            </svg>

            {/* Grid Container */}
            <div className="grid grid-cols-4 grid-rows-10 gap-6 h-full p-8 relative z-10">
                {/* Card 1: Baixar (Bottom Left Large) */}
                <motion.div
                    variants={fadeInUp}
                    className="col-span-1 row-span-6 row-start-5 bg-[#FDF8F3] rounded-[32px] overflow-hidden shadow-xl relative group"
                >
                    <img src={img1} alt="Baixar App" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#2D1810]/90 to-transparent pt-20">
                        <h3 className="text-white font-display font-bold text-2xl">{t('consumer.benefits.items.0.title')}</h3>
                        <p className="text-white/80 text-sm mt-1">{t('consumer.benefits.items.0.desc')}</p>
                    </div>
                </motion.div>

                {/* Card 2: Criar Conta (Top Left) */}
                <motion.div
                    variants={fadeInUp}
                    transition={{ delay: 0.1 }}
                    className="col-span-1 row-span-4 bg-[#F5EBE0] rounded-[32px] overflow-hidden shadow-lg relative group"
                >
                    <img src={img2} alt="Criar Conta" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    {/* Overlay hidden by default, shown on hover for cleaner look on smaller cards? Or always show? 
                       Reference has text ON the image. I'll use text overlay. */}
                    <div className="absolute top-0 left-0 right-0 p-6 text-center">
                        <h3 className="text-[#2D1810] font-display font-bold text-xl leading-tight">{t('consumer.benefits.items.1.title')}</h3>
                    </div>
                </motion.div>

                {/* Card 3: Descubrir (Top Center-Left) */}
                <motion.div
                    variants={fadeInUp}
                    transition={{ delay: 0.2 }}
                    className="col-span-1 row-span-4 bg-[#F5EBE0] rounded-[32px] overflow-hidden shadow-lg relative group"
                >
                    <img src={img3} alt="Descubrir" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute top-6 left-0 right-0 text-center px-4">
                        <h3 className="text-[#2D1810] font-display font-bold text-lg leading-tight">{t('consumer.benefits.items.2.title')}</h3>
                    </div>
                </motion.div>

                {/* Card 4: Cafeteria (Top Center-Right - Spans 1 col but wider visually in mockup? No, mockup shows it similar size to 3) */}
                <motion.div
                    variants={fadeInUp}
                    transition={{ delay: 0.3 }}
                    className="col-span-1 row-span-4 bg-[#EFE5DA] rounded-[32px] overflow-hidden shadow-lg relative group"
                >
                    <img src={img4} alt="Perto de você" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute top-6 left-0 right-0 text-center px-4">
                        <h3 className="text-[#2D1810] font-display font-bold text-lg leading-tight">{t('consumer.benefits.items.3.title')}</h3>
                    </div>
                </motion.div>

                {/* Card 8: Grão-Mestre (Right Column Full Height approx) */}
                <motion.div
                    variants={fadeInUp}
                    transition={{ delay: 0.4 }}
                    className="col-span-1 row-span-10 bg-gradient-to-b from-[#FDF8F3] to-[#F5EBE0] rounded-[32px] overflow-hidden shadow-2xl relative group border border-[#6F4E37]/10"
                >
                    <img src={img8} alt="Grão Mestre" className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute bottom-8 left-0 right-0 text-center px-6">
                        <h3 className="text-[#2D1810] font-display font-bold text-2xl mb-2">{t('consumer.benefits.items.7.title')}</h3>
                        <p className="text-[#6B5B4F] text-sm">{t('consumer.benefits.items.7.desc')}</p>
                    </div>
                </motion.div>

                {/* Card 5: QR Code (Middle Center - Wide) */}
                <motion.div
                    variants={fadeInUp}
                    transition={{ delay: 0.5 }}
                    className="col-span-2 row-span-3 bg-[#D7CCC8] rounded-[32px] overflow-hidden shadow-lg relative group"
                >
                    <img src={img5} alt="QR Code" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute top-6 left-8">
                        <h3 className="text-[#2D1810] font-display font-bold text-xl">{t('consumer.benefits.items.4.title')}</h3>
                    </div>
                </motion.div>

                {/* Card 6: Fidelidade (Bottom Center-Left) */}
                <motion.div
                    variants={fadeInUp}
                    transition={{ delay: 0.6 }}
                    className="col-span-1 row-span-3 bg-[#FDF8F3] rounded-[32px] overflow-hidden shadow-lg relative group"
                >
                    <img src={img6} alt="Fidelidade" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute top-4 left-0 right-0 text-center px-2">
                        <h3 className="text-[#2D1810] font-display font-bold text-base leading-tight">{t('consumer.benefits.items.5.title')}</h3>
                    </div>
                </motion.div>

                {/* Card 7: Café de Volta (Bottom Center-Right) */}
                <motion.div
                    variants={fadeInUp}
                    transition={{ delay: 0.7 }}
                    className="col-span-1 row-span-3 bg-[#FDF8F3] rounded-[32px] overflow-hidden shadow-lg relative group"
                >
                    <img src={img7} alt="Café de Volta" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute top-4 left-0 right-0 text-center px-2">
                        <h3 className="text-[#2D1810] font-display font-bold text-base leading-tight">{t('consumer.benefits.items.6.title')}</h3>
                    </div>
                </motion.div>

            </div>
        </div>
    );
}
